import React, { Component } from 'react'
import ReactDOM from 'react-dom'
import { withRouter } from 'react-router-dom'
import { observer, inject } from 'mobx-react'
import { reaction } from 'mobx'
import PropTypes from 'prop-types'
import map from 'lodash/map'
import range from 'lodash/range'

import ExpertSearchResults from './ExpertSearchResults'
import PlusIcon from '@components/Icon/PlusIcon'
import FilterGroup from '@components/FilterGroup'
import { buildNestedOpts } from '@atheneum/api/dist/lib'
import Scroll from '@components/Scroll'
import CollapsibleFilterGroup from '@components/CollapsibleFilterGroup'
import { Dropdown, TagsInput } from '@components/Autocomplete'
import { SearchPanelContainer } from '@components/Search'

import Radio from '@components/Radio'

import Tooltip from '@components/Tooltip/Tooltip'
import InfoIcon from '@components/Icon/Info'
import ExpertStatusIcon from '@components/ExpertStatusIcon'
import { ExpertProcessingStatus } from '../components/ExpertBulkCreate/helpers/expertProcessingStatus'
import { ExpertOptionField } from '../components/ExpertBulkCreate/helpers/expertOptionField'
import { ExpertFieldOptions } from '../components/ExpertBulkCreate/helpers/expertFieldOptions'
import {
  AddToProcessingActionEnum,
  AddToProcessingExpertTypeEnum,
  InitializeProcessingActionEnum,
  sendAddToProcessingMetric,
  sendInitializeProcessingMetric
} from '../components/ExpertBulkCreate/helpers/expertBulkCreate.metrics'
import { v4 as uuidv4 } from 'uuid'

const displayFlex = {
  display: 'flex'
}

class ExpertSearchPanel extends Component {
  static propTypes = {
    expertStore: PropTypes.object,
    expertSearchStore: PropTypes.object.isRequired,
    staticData: PropTypes.object.isRequired,
    modalStore: PropTypes.object.isRequired,
    expertBulkCreateStore: PropTypes.object.isRequired,
    location: PropTypes.object
  }

  constructor(props) {
    super(props)

    this.state = {
      expertBulkProcessingStatus: null
    }
    this.bulkCreateCreateIdentifier = `CREATE_${uuidv4()}`

    this.years = range(new Date().getFullYear(), 2013)

    this.expertSearchStore = props.expertSearchStore

    if (props.location.state && props.location.state.similarTo) {
      this.expertSearchStore.query.similarTo = props.location.state.similarTo
    }

    // Create list of all geographies
    this.geographies = props.staticData.countries.map(c => {
      return { value: c.name, label: c.name }
    })

    props.staticData.expertFocusRegions.forEach(r =>
      this.geographies.push({ value: r.name, label: r.name })
    )

    this.industries = []
    props.staticData.industries.forEach(i => {
      const subind = props.staticData.subIndustries.filter(
        si => si.industryId === i.id
      )
      this.industries.push({
        value: i.name,
        label: i.name,
        className: 'select-parent',
        children: subind.map(si => si.name).join(' ')
      })

      subind.forEach(si => {
        this.industries.push({
          value: si.name,
          label: si.name,
          className: 'select-child'
        })
      })
    })

    this.props.expertSearchStore.loadLists()

    this.registerReactions()

    this.processingExpertsDispose = props.expertBulkCreateStore.observeProcessingExperts(
      async experts => {
        const foundExpert = experts.find(
          e => e.customIdentifier === this.bulkCreateCreateIdentifier
        )
        if (foundExpert) {
          this.setState((prevState, props) => {
            return {
              expertBulkProcessingStatus: ExpertProcessingStatus.Processing
            }
          })
        }
      }
    )

    this.completedExpertsDispose = props.expertBulkCreateStore.observeCompletedExperts(
      async experts => {
        const foundExpert = experts.find(
          e => e.customIdentifier === this.bulkCreateCreateIdentifier
        )
        if (foundExpert) {
          await props.expertStore.loadData(foundExpert.data.id)
          props.history.push('/expert-search/expert/' + foundExpert.data.id)

          this.setState((prevState, props) => {
            return {
              expertBulkProcessingStatus: ExpertProcessingStatus.Completed
            }
          })
        }
      }
    )

    this.errorExpertsDispose = props.expertBulkCreateStore.observeErrorExperts(
      async experts => {
        const foundExpert = experts.find(
          e => e.customIdentifier === this.bulkCreateCreateIdentifier
        )
        if (foundExpert) {
          this.setState((prevState, props) => {
            return {
              expertBulkProcessingStatus: ExpertProcessingStatus.Error
            }
          })
        }
      }
    )

    this.cancelledExpertsDispose = props.expertBulkCreateStore.observeCancelledExperts(
      async experts => {
        const foundExpert = experts.find(
          e => e.customIdentifier === this.bulkCreateCreateIdentifier
        )
        if (foundExpert) {
          this.setState((prevState, props) => {
            return {
              expertBulkProcessingStatus: ExpertProcessingStatus.Cancelled
            }
          })
        }
      }
    )
  }

  componentWillUnmount() {
    this.processingExpertsDispose()
    this.completedExpertsDispose()
    this.errorExpertsDispose()
    this.cancelledExpertsDispose()
  }

  componentDidUpdate() {
    const { zoomTo } = this.props.expertSearchStore
    const similarExpertNode = ReactDOM.findDOMNode(this.similarExpertsRef)
    if (zoomTo) {
      similarExpertNode.scrollIntoView()
      setTimeout(() => {
        this.props.expertSearchStore.zoomTo = false
      }, 1500)
    }
  }

  onClearSearch = () => {
    this.expertSearchStore.clear()
    // clear highlights
    const { expertStore } = this.props
    if (expertStore.id && expertStore.expertCopy.highlight) {
      expertStore.expertCopy = expertStore.expert
    }
  }

  onRecomendationClick = (item, key) => {
    this.props.expertSearchStore.query[key].push({
      label: item.value,
      value: item.value
    })
  }

  registerReactions() {
    this.reactions = []
    ;[
      'companies',
      'positions',
      'country',
      'industry',
      'languages',
      'similarTo',
      'suggestedFor'
    ].forEach(key => {
      this.reactions.push(
        reaction(
          () => this.expertSearchStore.query[key].map(i => i.value),
          () => this.search()
        )
      )
    })

    this.reactions.push(
      reaction(
        () =>
          [
            'experiencePeriod',
            'interviewed',
            'hasEmail',
            'hasApp',
            'hasPersonalPhone',
            'hasOfficePhone',
            'hasMobilePhone',
            'hasDirectPhone',
            'hasWhatsapp',
            'proficiency',
            'complianceFilter',
            'hfComplianceFilter',
            'expertStatus',
            'includeScreeningInformation'
          ].map(i => this.expertSearchStore.query[i]),
        () => this.search()
      )
    )
  }

  searchCompanies = input => {
    return this.expertSearchStore.autocomplete(input, 'company')
  }

  searchExperts = input => {
    return this.expertSearchStore.expertsAutocomplete(input)
  }

  searchProjects = input => {
    return this.expertSearchStore.projectsAutocomplete(input)
  }

  searchPositions = input => {
    return this.expertSearchStore.autocomplete(input, 'position')
  }

  geographiesChange = val => {
    this.expertSearchStore.query.country = val.map(i => {
      return { value: i.value, label: i.value }
    })
  }

  industriesChange = val => {
    this.expertSearchStore.query.industry = val.map(i => {
      return { value: i.value, label: i.value }
    })
  }

  companiesChange = val => {
    this.expertSearchStore.query.companies = val.map(i => {
      return { value: i.value, label: i.value }
    })
  }

  expertChange = val => {
    this.expertSearchStore.query.similarTo.replace(val)
  }

  suggestedForChange = val => {
    this.expertSearchStore.query.suggestedFor.replace(val)
  }

  positionsChange = val => {
    this.expertSearchStore.query.positions = val.map(i => {
      return { value: i.value, label: i.value }
    })
  }

  search = () => {
    return this.expertSearchStore.search()
  }

  renderCountryOrRegionItem = item => {
    if (item.type === 'expertFocusRegions') {
      return <strong>{item.label}</strong>
    }

    return <span className="autocomplete__item--suboption">{item.label}</span>
  }

  renderIndustryOrSubindustry = item => {
    if (item.type === 'industries') {
      return <strong>{item.label}</strong>
    }

    return <span className="autocomplete__item--suboption">{item.label}</span>
  }

  searchOnEnter = e => {
    if (e.key === 'Enter') {
      return this.search()
    }
  }

  onCheckboxFilter(e, key) {
    this.expertSearchStore.query[key] = !!e.target.checked
  }

  onRadioButtonFilter(value, key) {
    this.expertSearchStore.query[key] = !value ? undefined : Number(value)
  }

  createdInCheckbox = year => {
    const onYearChange = e => {
      if (e.target.checked) {
        this.expertSearchStore.query.createdIn =
          this.expertSearchStore.query.createdIn || []
        this.expertSearchStore.query.createdIn.push(year)
      } else {
        this.expertSearchStore.query.createdIn =
          this.expertSearchStore.query.createdIn || []
        const index = this.expertSearchStore.query.createdIn.lastIndexOf(year)
        if (index > -1) {
          this.expertSearchStore.query.createdIn.splice(index, 1)
        }
      }
      this.search()
    }

    return (
      <div key={year}>
        <label className="checkbox">
          <input
            type="checkbox"
            value="true"
            onChange={onYearChange}
            checked={this.expertSearchStore.query.createdIn.includes(year)}
          />
          {year}
        </label>
      </div>
    )
  }

  yearCheckbox = year => {
    const onYearChange = e => {
      if (e.target.checked) {
        this.expertSearchStore.query.lastInterviewedIn =
          this.expertSearchStore.query.lastInterviewedIn || []
        this.expertSearchStore.query.lastInterviewedIn.push(year)
      } else {
        this.expertSearchStore.query.lastInterviewedIn =
          this.expertSearchStore.query.lastInterviewedIn || []
        const index = this.expertSearchStore.query.lastInterviewedIn.lastIndexOf(
          year
        )
        if (index > -1) {
          this.expertSearchStore.query.lastInterviewedIn.splice(index, 1)
        }
      }
      this.search()
    }

    return (
      <div key={year}>
        <label className="checkbox">
          <input
            type="checkbox"
            value="true"
            onChange={onYearChange}
            checked={this.expertSearchStore.query.lastInterviewedIn.includes(
              year
            )}
          />
          {year}
        </label>
      </div>
    )
  }

  renderSearchStatus = (query, staticData) => {
    const { EXPERT_STATUSES } = staticData.CONST

    return (
      <>
        <div>
          <label className="checkbox" style={{ fontWeight: 'bold' }}>
            <input
              type="checkbox"
              value="true"
              checked={
                query.expertStatus.coldProspect ||
                query.expertStatus.warmProspect ||
                query.expertStatus.dormantProspect
              }
              onChange={e => {
                const checked = !(
                  query.expertStatus.coldProspect ||
                  query.expertStatus.warmProspect ||
                  query.expertStatus.dormantProspect
                )

                query.expertStatus = {
                  ...query.expertStatus,
                  coldProspect: checked,
                  warmProspect: checked,
                  dormantProspect: checked
                }
              }}
            />
            PROSPECTS
          </label>
          <Tooltip
            tooltip={
              <div style={{ maxWidth: '250px' }}>
                The person has not signed the latest compliance and has never
                been scheduled on a project.
              </div>
            }>
            <div className="iconWrapper">
              <InfoIcon />
            </div>
          </Tooltip>
        </div>
        <div>
          <div style={displayFlex}>
            <ExpertStatusIcon expertStatusId={EXPERT_STATUSES.COLD_PROSPECT} />
            <label className="checkbox">
              <input
                type="checkbox"
                value="true"
                checked={query.expertStatus.coldProspect}
                onChange={e => {
                  query.expertStatus = {
                    ...query.expertStatus,
                    coldProspect: Boolean(e.target.checked)
                  }
                }}
              />
              Cold
            </label>
            <Tooltip
              tooltip={
                <div style={{ maxWidth: '250px' }}>
                  Never added to a project or never reacted to a project invite.
                </div>
              }>
              <div className="iconWrapper">
                <InfoIcon />
              </div>
            </Tooltip>
          </div>
          <div>
            <ExpertStatusIcon expertStatusId={EXPERT_STATUSES.WARM_PROSPECT} />
            <label className="checkbox">
              <input
                type="checkbox"
                value="true"
                checked={query.expertStatus.warmProspect}
                onChange={e => {
                  query.expertStatus = {
                    ...query.expertStatus,
                    warmProspect: Boolean(e.target.checked)
                  }
                }}
              />
              Warm
            </label>
            <Tooltip
              tooltip={
                <div style={{ maxWidth: '250px' }}>
                  Reacted positively to a project invite (reply status =
                  Interested, Applied or Confirmed) or added following the
                  referral from another expert.
                </div>
              }>
              <div className="iconWrapper">
                <InfoIcon />
              </div>
            </Tooltip>
          </div>
          <div>
            <ExpertStatusIcon
              expertStatusId={EXPERT_STATUSES.DORMANT_PROSPECT}
            />
            <label className="checkbox">
              <input
                type="checkbox"
                value="true"
                checked={query.expertStatus.dormantProspect}
                onChange={e => {
                  query.expertStatus = {
                    ...query.expertStatus,
                    dormantProspect: Boolean(e.target.checked)
                  }
                }}
              />
              Dormant
            </label>
            <Tooltip
              tooltip={
                <div style={{ maxWidth: '250px' }}>
                  Previously warm prospect but now over 2 years from the last
                  project.
                </div>
              }>
              <div className="iconWrapper">
                <InfoIcon />
              </div>
            </Tooltip>
          </div>
        </div>
        <div>
          <label className="checkbox" style={{ fontWeight: 'bold' }}>
            <input
              type="checkbox"
              value="true"
              checked={
                query.expertStatus.activeExpert ||
                query.expertStatus.dormantExpert
              }
              onChange={e => {
                const checked = !(
                  query.expertStatus.activeExpert ||
                  query.expertStatus.dormantExpert
                )

                query.expertStatus = {
                  ...query.expertStatus,
                  activeExpert: checked,
                  dormantExpert: checked
                }
              }}
            />
            EXPERTS
          </label>
          <Tooltip
            tooltip={
              <div style={{ maxWidth: '250px' }}>
                The person has signed the latest compliance or has been
                scheduled on a project.
              </div>
            }>
            <div className="iconWrapper">
              <InfoIcon />
            </div>
          </Tooltip>
        </div>
        <div>
          <div>
            <ExpertStatusIcon expertStatusId={EXPERT_STATUSES.ACTIVE_EXPERT} />
            <label className="checkbox">
              <input
                type="checkbox"
                value="true"
                checked={query.expertStatus.activeExpert}
                onChange={e => {
                  query.expertStatus = {
                    ...query.expertStatus,
                    activeExpert: Boolean(e.target.checked)
                  }
                }}
              />
              Active
            </label>
            <Tooltip
              tooltip={
                <div style={{ maxWidth: '250px' }}>
                  Signed the latest compliance in the last 5 years or scheduled
                  on a project and active in the last 2 years.
                </div>
              }>
              <div className="iconWrapper">
                <InfoIcon />
              </div>
            </Tooltip>
          </div>
          <div>
            <ExpertStatusIcon expertStatusId={EXPERT_STATUSES.DORMANT_EXPERT} />
            <label className="checkbox">
              <input
                type="checkbox"
                value="true"
                checked={query.expertStatus.dormantExpert}
                onChange={e => {
                  query.expertStatus = {
                    ...query.expertStatus,
                    dormantExpert: Boolean(e.target.checked)
                  }
                }}
              />
              Dormant
            </label>
            <Tooltip
              tooltip={
                <div style={{ maxWidth: '250px' }}>
                  Previously active expert but now over 2 years from the last
                  project (latest compliance not signed).
                </div>
              }>
              <div className="iconWrapper">
                <InfoIcon />
              </div>
            </Tooltip>
          </div>
        </div>
        <div>
          <label className="checkbox" style={{ fontWeight: 'bold' }}>
            <input
              type="checkbox"
              value="true"
              checked={query.expertStatus.notContact}
              onChange={e => {
                query.expertStatus = {
                  ...query.expertStatus,
                  notContact: Boolean(e.target.checked)
                }
              }}
            />
            DO NOT CONTACT
          </label>
          <ExpertStatusIcon expertStatusId={EXPERT_STATUSES.DO_NOT_CONTACT} />
        </div>
      </>
    )
  }
  render() {
    const { staticData, expertSearchStore, expertBulkCreateStore } = this.props
    const { zoomTo } = expertSearchStore
    const query = this.expertSearchStore.query
    const complianceFilter = staticData.complianceFilter

    return (
      <div className="expert-search-panel">
        <div className="expert-search-panel-container">
          <SearchPanelContainer
            buttonProps={{
              disabled:
                this.state.expertBulkProcessingStatus ===
                ExpertProcessingStatus.Processing,
              dataCy: 'create-expert-btn',
              buttonText: 'Create new expert',
              onClick: async () => {
                sendInitializeProcessingMetric(
                  InitializeProcessingActionEnum.MANUAL_CREATE_USER
                )
                sendAddToProcessingMetric(
                  AddToProcessingActionEnum.MANUAL_CREATE_USER,
                  AddToProcessingExpertTypeEnum.NON_EXISTING_EXPERT
                )

                await expertBulkCreateStore.addExpertsToProcessingQueue(
                  [
                    {
                      ...expertBulkCreateStore.createEmptyExpert(),
                      bulkCreateCustomIdentifier: this
                        .bulkCreateCreateIdentifier
                    }
                  ],
                  ExpertFieldOptions.EXPERT_CREATION_OPTIONS,
                  ExpertOptionField.GetAllFields(),
                  true,
                  false,
                  true
                )
              }
            }}
            searchTitle="Search Experts"
            onClearSearch={this.onClearSearch}>
            <FilterGroup
              label="Keyword or phrase"
              tooltipContent={
                <div style={{ maxWidth: '250px' }}>
                  {`This search looks at the expert's experience description and full details (positions and related info),
                  plus screening information (if the option below is selected).`}
                  <br />
                  {`TIP: Searches can be refined using quotes ("") for exact matches, asterisks ( * ) for wildcards,
                  Boolean operators (AND, OR, NOT) and parenthesis to combine all of the above in a single query.`}
                </div>
              }
              onSearch={this.search}>
              <input
                onKeyDown={this.searchOnEnter}
                onChange={e =>
                  (this.expertSearchStore.query.keyword = e.target.value)
                }
                value={query.keyword}
              />
            </FilterGroup>
            <FilterGroup>
              <div>
                <label className="checkbox">
                  <input
                    type="checkbox"
                    value="true"
                    checked={query.includeScreeningInformation}
                    onChange={e =>
                      this.onCheckboxFilter(e, 'includeScreeningInformation')
                    }
                  />
                  Include screening information
                </label>
              </div>
            </FilterGroup>
            <FilterGroup
              label="Name, Original name, phone, email or code"
              onSearch={this.search}>
              <input
                onKeyDown={this.searchOnEnter}
                onChange={e =>
                  (this.expertSearchStore.query.expertData = e.target.value)
                }
                value={query.expertData}
              />
            </FilterGroup>
            <FilterGroup>
              <div>
                <label className="checkbox">
                  <input
                    type="checkbox"
                    value="true"
                    checked={query.interviewed}
                    onChange={e => this.onCheckboxFilter(e, 'interviewed')}
                  />
                  Interviewed
                </label>
              </div>
            </FilterGroup>
            <div>
              <hr className="hr-separator hr-separator--light"></hr>
              <label className="checkbox m-b-lg">
                <input
                  type="checkbox"
                  value="true"
                  checked={this.expertSearchStore.openLinkedinOnPin}
                  onChange={() => {
                    localStorage.setItem(
                      'openLinkedinOnPin',
                      JSON.stringify(!this.expertSearchStore.openLinkedinOnPin)
                    )
                    this.expertSearchStore.openLinkedinOnPin = !this
                      .expertSearchStore.openLinkedinOnPin
                  }}
                />
                Open LinkedIn while pinning
              </label>
            </div>
            <CollapsibleFilterGroup title="Last Interviewed In">
              <div>{this.years.map(y => this.yearCheckbox(y))}</div>
            </CollapsibleFilterGroup>

            <CollapsibleFilterGroup title="Status" open>
              {this.renderSearchStatus(query, staticData)}
            </CollapsibleFilterGroup>

            <CollapsibleFilterGroup title="Compliance" open>
              <FilterGroup label="Compliance">
                <div>
                  <div
                    className="radio-button-input"
                    onClick={() =>
                      this.onRadioButtonFilter(
                        complianceFilter.noFilter,
                        'complianceFilter'
                      )
                    }>
                    <Radio
                      checked={
                        query.complianceFilter === complianceFilter.noFilter
                      }
                    />
                    <label>No filter</label>
                  </div>
                </div>

                <div>
                  <div
                    className="radio-button-input"
                    onClick={() =>
                      this.onRadioButtonFilter(
                        complianceFilter.withCompliance,
                        'complianceFilter'
                      )
                    }>
                    <Radio
                      checked={
                        query.complianceFilter ===
                        complianceFilter.withCompliance
                      }
                    />
                    <label>With compliance</label>
                  </div>
                </div>

                <div>
                  <div
                    className="radio-button-input"
                    onClick={() =>
                      this.onRadioButtonFilter(
                        complianceFilter.withoutCompliance,
                        'complianceFilter'
                      )
                    }>
                    <Radio
                      checked={
                        query.complianceFilter ===
                        complianceFilter.withoutCompliance
                      }
                    />
                    <label>Without compliance</label>
                  </div>
                </div>
              </FilterGroup>

              <FilterGroup label="HF Compliance">
                <div>
                  <div
                    className="radio-button-input"
                    onClick={() =>
                      this.onRadioButtonFilter(
                        complianceFilter.noFilter,
                        'hfComplianceFilter'
                      )
                    }>
                    <Radio
                      checked={
                        query.hfComplianceFilter === complianceFilter.noFilter
                      }
                    />
                    <label>No filter</label>
                  </div>
                </div>

                <div>
                  <div
                    className="radio-button-input"
                    onClick={() =>
                      this.onRadioButtonFilter(
                        complianceFilter.withCompliance,
                        'hfComplianceFilter'
                      )
                    }>
                    <Radio
                      checked={
                        query.hfComplianceFilter ===
                        complianceFilter.withCompliance
                      }
                    />
                    <label>With HF compliance</label>
                  </div>
                </div>

                <div>
                  <div
                    className="radio-button-input"
                    onClick={() =>
                      this.onRadioButtonFilter(
                        complianceFilter.withoutCompliance,
                        'hfComplianceFilter'
                      )
                    }>
                    <Radio
                      checked={
                        query.hfComplianceFilter ===
                        complianceFilter.withoutCompliance
                      }
                    />
                    <label>Without HF compliance</label>
                  </div>
                </div>
              </FilterGroup>
            </CollapsibleFilterGroup>

            <CollapsibleFilterGroup title="Company" open>
              <div className="m-b-md">
                <TagsInput
                  creatable
                  placeholder="Type or select company"
                  value={this.expertSearchStore.query.companies}
                  itemToString={item => item && item.label}
                  itemFromCreatable={item => ({ value: item, label: item })}
                  loadOptions={this.searchCompanies}
                  onChange={this.companiesChange}
                  debounce={500}
                />
              </div>
              <div>
                {map(
                  this.props.expertSearchStore.companyRecomandations,
                  (c, i) => {
                    return i < 4 ? (
                      <div className="search-panel-recomendation" key={c.value}>
                        <span
                          className="row--link"
                          onClick={() =>
                            this.onRecomendationClick(c, 'companies')
                          }>
                          <PlusIcon /> {c.value}
                        </span>
                      </div>
                    ) : (
                      ''
                    )
                  }
                )}
              </div>
            </CollapsibleFilterGroup>

            <CollapsibleFilterGroup title="Position" open>
              <div className="m-b-md">
                <TagsInput
                  creatable
                  placeholder="Type or select position"
                  value={this.expertSearchStore.query.positions}
                  itemToString={item => item && item.label}
                  itemFromCreatable={item => ({ value: item, label: item })}
                  loadOptions={this.searchPositions}
                  onChange={this.positionsChange}
                  debounce={500}
                />
              </div>
              <div>
                {map(
                  this.props.expertSearchStore.positionRecomandations,
                  (c, i) => {
                    return i < 4 ? (
                      <div className="search-panel-recomendation" key={c.value}>
                        <span
                          className="row--link"
                          onClick={() =>
                            this.onRecomendationClick(c, 'positions')
                          }>
                          <PlusIcon /> {c.value}
                        </span>
                      </div>
                    ) : (
                      ''
                    )
                  }
                )}
              </div>
            </CollapsibleFilterGroup>

            <CollapsibleFilterGroup title="Experience Period">
              <Dropdown
                valueIsId
                dropdownClassName="autocomplete__input--blue-text"
                itemToString={item => item && item.label}
                options={[
                  { id: 'ALL', label: 'Present and past positions' },
                  { id: 'CURRENT', label: 'Present positions only' },
                  { id: 'PAST', label: 'Past positions only' }
                ]}
                selectedItem={this.expertSearchStore.query.experiencePeriod}
                onChange={option =>
                  (this.expertSearchStore.query.experiencePeriod = option.id)
                }
              />
            </CollapsibleFilterGroup>

            <CollapsibleFilterGroup title="Geography">
              <div>
                <TagsInput
                  value={query.country}
                  placeholder="Select country or region"
                  options={buildNestedOpts(
                    'expertFocusRegions',
                    'countries',
                    'expertFocusRegionId'
                  )}
                  filterItemsOnly
                  itemToString={item => item && item.label}
                  renderItem={this.renderCountryOrRegionItem}
                  onChange={this.geographiesChange}
                />
              </div>
            </CollapsibleFilterGroup>

            <CollapsibleFilterGroup title="Industries" open>
              <div>
                <TagsInput
                  value={query.industry}
                  placeholder="Select industry"
                  options={buildNestedOpts(
                    'industries',
                    'subIndustries',
                    'industryId'
                  )}
                  filterItemsOnly
                  itemToString={item => item && item.label}
                  renderItem={this.renderIndustryOrSubindustry}
                  onChange={this.industriesChange}
                />
              </div>
            </CollapsibleFilterGroup>

            <CollapsibleFilterGroup title="Languages">
              <div>
                <div className="m-b-md">
                  <TagsInput
                    value={query.languages}
                    itemToString={item => item && item.name}
                    onChange={lang => {
                      query.languages = lang
                    }}
                    options={staticData.languages}
                  />
                </div>
                <Dropdown
                  selectedItem={query.proficiency}
                  placeholder="Proficiency"
                  itemToString={item => item && item.name}
                  onChange={profic => {
                    query.proficiency = profic
                  }}
                  options={staticData.languageProficiencies}
                />
              </div>
            </CollapsibleFilterGroup>

            <CollapsibleFilterGroup title="Contacts">
              <div>
                <label className="checkbox">
                  <input
                    type="checkbox"
                    value="true"
                    checked={query.hasEmail}
                    onChange={e => this.onCheckboxFilter(e, 'hasEmail')}
                  />
                  Email
                </label>
              </div>
              <div>
                <label className="checkbox">
                  <input
                    type="checkbox"
                    value="true"
                    checked={query.hasApp}
                    onChange={e => this.onCheckboxFilter(e, 'hasApp')}
                  />
                  App
                </label>
              </div>
              <div>
                <label className="checkbox">
                  <input
                    type="checkbox"
                    value="true"
                    checked={query.hasPersonalPhone}
                    onChange={e => this.onCheckboxFilter(e, 'hasPersonalPhone')}
                  />
                  Personal phone
                </label>
              </div>
              <div>
                <label className="checkbox">
                  <input
                    type="checkbox"
                    value="true"
                    checked={query.hasOfficePhone}
                    onChange={e => this.onCheckboxFilter(e, 'hasOfficePhone')}
                  />
                  Office phone
                </label>
              </div>
              <div>
                <label className="checkbox">
                  <input
                    type="checkbox"
                    value="true"
                    checked={query.hasMobilePhone}
                    onChange={e => this.onCheckboxFilter(e, 'hasMobilePhone')}
                  />
                  Mobile phone
                </label>
              </div>
              <div>
                <label className="checkbox">
                  <input
                    type="checkbox"
                    value="true"
                    checked={query.hasDirectPhone}
                    onChange={e => this.onCheckboxFilter(e, 'hasDirectPhone')}
                  />
                  Direct phone
                </label>
              </div>
              <div>
                <label className="checkbox">
                  <input
                    type="checkbox"
                    value="true"
                    checked={query.hasWhatsapp}
                    onChange={e => this.onCheckboxFilter(e, 'hasWhatsapp')}
                  />
                  WhatsApp
                </label>
              </div>
            </CollapsibleFilterGroup>

            <CollapsibleFilterGroup title="Created in">
              <div>{this.years.map(y => this.createdInCheckbox(y))}</div>
            </CollapsibleFilterGroup>

            <div className={zoomTo ? 'similar-experts--active' : ''}>
              <CollapsibleFilterGroup title="Experts similar to" open>
                <div
                  ref={ref => {
                    this.similarExpertsRef = ref
                  }}>
                  <TagsInput
                    value={this.expertSearchStore.query.similarTo}
                    placeholder="Select expert"
                    itemToString={item => item && item.label}
                    loadOptions={this.searchExperts}
                    onChange={this.expertChange}
                  />
                </div>
              </CollapsibleFilterGroup>
            </div>
            <CollapsibleFilterGroup title="Experts delivered on projects" open>
              <div>
                <TagsInput
                  value={this.expertSearchStore.query.suggestedFor}
                  placeholder="Select projects"
                  itemToString={item => item && item.label}
                  loadOptions={this.searchProjects}
                  onChange={this.suggestedForChange}
                />
              </div>
            </CollapsibleFilterGroup>
          </SearchPanelContainer>
          <div className="expert-search-panel-results">
            <Scroll>
              <ExpertSearchResults
                expertSearchStore={this.expertSearchStore}
                total={this.expertSearchStore.total}
              />
            </Scroll>
          </div>
        </div>
      </div>
    )
  }
}

export default withRouter(
  inject(
    'expertStore',
    'expertSearchStore',
    'modalStore',
    'staticData',
    'expertBulkCreateStore'
  )(observer(ExpertSearchPanel))
)
